import {
  Directive, Input, TemplateRef, ElementRef,
  HostListener, OnDestroy, ViewContainerRef, OnChanges, SimpleChanges
} from '@angular/core';
import { Overlay, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

export type TooltipPlacement =
  | 'top' | 'top-start' | 'top-end'
  | 'bottom' | 'bottom-start' | 'bottom-end'
  | 'left' | 'left-start' | 'left-end'
  | 'right' | 'right-start' | 'right-end';

const PLACEMENT_POSITIONS: Record<TooltipPlacement, ConnectedPosition[]> = {
  'top':          [{ originX: 'center', originY: 'top',    overlayX: 'center', overlayY: 'bottom', offsetY: -8 }],
  'top-start':    [{ originX: 'start',  originY: 'top',    overlayX: 'start',  overlayY: 'bottom', offsetY: -8 }],
  'top-end':      [{ originX: 'end',    originY: 'top',    overlayX: 'end',    overlayY: 'bottom', offsetY: -8 }],
  'bottom':       [{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top',    offsetY:  8 }],
  'bottom-start': [{ originX: 'start',  originY: 'bottom', overlayX: 'start',  overlayY: 'top',    offsetY:  8 }],
  'bottom-end':   [{ originX: 'end',    originY: 'bottom', overlayX: 'end',    overlayY: 'top',    offsetY:  8 }],
  'left':         [{ originX: 'start',  originY: 'center', overlayX: 'end',    overlayY: 'center', offsetX: -8 }],
  'left-start':   [{ originX: 'start',  originY: 'top',    overlayX: 'end',    overlayY: 'top',    offsetX: -8 }],
  'left-end':     [{ originX: 'start',  originY: 'bottom', overlayX: 'end',    overlayY: 'bottom', offsetX: -8 }],
  'right':        [{ originX: 'end',    originY: 'center', overlayX: 'start',  overlayY: 'center', offsetX:  8 }],
  'right-start':  [{ originX: 'end',    originY: 'top',    overlayX: 'start',  overlayY: 'top',    offsetX:  8 }],
  'right-end':    [{ originX: 'end',    originY: 'bottom', overlayX: 'start',  overlayY: 'bottom', offsetX:  8 }],
};

const AXIS_FALLBACK: Record<string, TooltipPlacement[]> = {
  top:    ['top', 'bottom', 'right', 'left'],
  bottom: ['bottom', 'top', 'right', 'left'],
  left:   ['left', 'right', 'top', 'bottom'],
  right:  ['right', 'left', 'top', 'bottom'],
};

function getPositionsWithFallbacks(placement: TooltipPlacement): ConnectedPosition[] {
  const primary = placement.split('-')[0];
  const suffix = placement.includes('-start') ? '-start'
               : placement.includes('-end')   ? '-end'
               : '';
  const chain = AXIS_FALLBACK[primary] ?? ['bottom', 'top', 'right', 'left'];

  return chain.flatMap(p => {
    const key = `${p}${suffix}` as TooltipPlacement;
    return PLACEMENT_POSITIONS[key] ?? PLACEMENT_POSITIONS[p as TooltipPlacement];
  });
}

@Directive({
  selector: '[appCustomTooltip]',
  standalone: false,
})
export class CustomTooltipDirective implements OnDestroy, OnChanges {
  @Input('appCustomTooltip') tooltipTemplate!: TemplateRef<any>;
  @Input() placement: TooltipPlacement = 'top';
  @Input() disableTooltip: boolean = false;

  private overlayRef: OverlayRef | null = null;

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef,
    private vcr: ViewContainerRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disableTooltip'] && changes['disableTooltip'].currentValue === true) {
      this.overlayRef?.detach();
    }
  }

  @HostListener('mouseenter') onMouseEnter() {
    if (this.disableTooltip) return;

    const ref = this.getOrCreateOverlay();
    if (ref.hasAttached()) return;
    ref.attach(new TemplatePortal(this.tooltipTemplate, this.vcr));
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.overlayRef?.detach();
  }

  ngOnDestroy() {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  private getOrCreateOverlay(): OverlayRef {
    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        panelClass: 'custom-tooltip-content',
        positionStrategy: this.overlay
          .position()
          .flexibleConnectedTo(this.elementRef)
          .withPositions(getPositionsWithFallbacks(this.placement))
          .withFlexibleDimensions(false)
          .withPush(false),
        scrollStrategy: this.overlay.scrollStrategies.close(),
      });
    }
    return this.overlayRef;
  }
}